/**
 * VoiceDetectionManager - Manages the voice detection screen UI and logic
 * Orchestrates the HappyBirthdaySingAlong component with the funnel UI
 */

import {
  HappyBirthdaySingAlong,
  type State,
  type WordState,
  type SingAlongResult,
  type LyricLine
} from './happy-birthday-sing-along';
import { VOICE_TYPE_CONTENT, type VoiceTypeContent } from './voice-type-content';
import { playCelebration } from '../decorations/celebration-animations';

interface VoiceDetectionDependencies {
  userRepository: any;
  statsManager: any;
}

class VoiceDetectionManager {
  private deps: VoiceDetectionDependencies | null = null;
  private singAlong: HappyBirthdaySingAlong | null = null;
  public isInitialized = false;

  // DOM element references
  private screenElement: HTMLElement | null = null;
  private readyState: HTMLElement | null = null;
  private countdownState: HTMLElement | null = null;
  private singingState: HTMLElement | null = null;
  private resultsState: HTMLElement | null = null;
  private errorState: HTMLElement | null = null;

  private countdownDisplay: HTMLElement | null = null;
  private lyricsContainer: HTMLElement | null = null;
  private pitchNote: HTMLElement | null = null;
  private pitchFrequency: HTMLElement | null = null;
  private audioLevelBar: HTMLElement | null = null;
  private resultVoiceType: HTMLElement | null = null;
  private resultPitchNote: HTMLElement | null = null;
  private resultDescription: HTMLElement | null = null;
  private resultVibe: HTMLElement | null = null;
  private resultExamples: HTMLElement | null = null;
  private resultExplanation: HTMLElement | null = null;
  private resultPromise: HTMLElement | null = null;
  private errorMessage: HTMLElement | null = null;

  private startBtn: HTMLButtonElement | null = null;
  private continueBtn: HTMLButtonElement | null = null;
  private tryAgainBtn: HTMLButtonElement | null = null;
  private skipBtn: HTMLButtonElement | null = null;

  init(deps: VoiceDetectionDependencies): void {
    this.deps = deps;
    this.isInitialized = true;
    console.log('VoiceDetectionManager initialized');
  }

  /**
   * Called when voice-detection screen becomes active
   */
  onScreenEnter(screenElement: HTMLElement): void {
    this.screenElement = screenElement;
    this.bindDOMElements();
    this.createSingAlong();
    this.renderLyrics();
    this.updateUI('ready');
  }

  /**
   * Called when leaving the screen
   */
  onScreenLeave(): void {
    this.destroy();
  }

  private bindDOMElements(): void {
    if (!this.screenElement) return;

    // State containers
    this.readyState = this.screenElement.querySelector('#voice-detection-ready');
    this.countdownState = this.screenElement.querySelector('#voice-detection-countdown');
    this.singingState = this.screenElement.querySelector('#voice-detection-singing');
    this.resultsState = this.screenElement.querySelector('#voice-detection-results');
    this.errorState = this.screenElement.querySelector('#voice-detection-error');

    // Countdown
    this.countdownDisplay = this.screenElement.querySelector('#countdown-display');

    // Singing state elements
    this.lyricsContainer = this.screenElement.querySelector('#lyrics-container');
    this.pitchNote = this.screenElement.querySelector('#pitch-note');
    this.pitchFrequency = this.screenElement.querySelector('#pitch-frequency');
    this.audioLevelBar = this.screenElement.querySelector('#audio-level-bar');

    // Results state elements
    this.resultVoiceType = this.screenElement.querySelector('#result-voice-type');
    this.resultPitchNote = this.screenElement.querySelector('#result-pitch-note');
    this.resultDescription = this.screenElement.querySelector('#result-description');
    this.resultVibe = this.screenElement.querySelector('#result-vibe');
    this.resultExamples = this.screenElement.querySelector('#result-examples');
    this.resultExplanation = this.screenElement.querySelector('#result-explanation');
    this.resultPromise = this.screenElement.querySelector('#result-promise');

    // Error state
    this.errorMessage = this.screenElement.querySelector('#error-message');

    // Buttons
    this.startBtn = this.screenElement.querySelector('#voice-detection-start-btn');
    this.continueBtn = this.screenElement.querySelector('#voice-detection-continue-btn');
    this.tryAgainBtn = this.screenElement.querySelector('#voice-detection-tryagain-btn');
    this.skipBtn = this.screenElement.querySelector('#voice-detection-skip-btn');
  }

  private createSingAlong(): void {
    this.singAlong = new HappyBirthdaySingAlong({
      onStateChange: (state) => this.handleStateChange(state),
      onCountdownTick: (count) => this.handleCountdownTick(count),
      onPitchDetected: (freq, conf, note) => this.handlePitchDetected(freq, conf, note),
      onWordAdvance: (index, state) => this.handleWordAdvance(index, state),
      onAudioLevel: (level) => this.handleAudioLevel(level),
      onResult: (result) => this.handleResult(result),
      onError: (error) => this.handleError(error),
      onNameDetected: (name, wordIndex) => this.handleNameDetected(name, wordIndex),
    });
  }

  private renderLyrics(): void {
    if (!this.lyricsContainer || !this.singAlong) return;

    const lyrics = this.singAlong.getLyrics();
    let wordIndex = 0;

    this.lyricsContainer.innerHTML = '';

    lyrics.forEach((line: LyricLine) => {
      const lineDiv = document.createElement('div');
      lineDiv.className = 'lyric-line';

      line.words.forEach((word) => {
        const wordSpan = document.createElement('span');
        wordSpan.className = 'lyric-word lyric-word--upcoming';
        wordSpan.dataset.wordIndex = String(wordIndex);
        wordSpan.textContent = word.text;
        lineDiv.appendChild(wordSpan);
        wordIndex++;
      });

      this.lyricsContainer!.appendChild(lineDiv);
    });

    // Set first word as current
    const firstWord = this.lyricsContainer.querySelector('[data-word-index="0"]');
    if (firstWord) {
      firstWord.classList.remove('lyric-word--upcoming');
      firstWord.classList.add('lyric-word--current');
    }
  }

  private updateUI(state: State): void {
    // Hide all states
    this.readyState?.classList.add('hidden');
    this.countdownState?.classList.add('hidden');
    this.singingState?.classList.add('hidden');
    this.resultsState?.classList.add('hidden');
    this.errorState?.classList.add('hidden');

    // Hide all buttons
    this.startBtn?.classList.add('hidden');
    this.continueBtn?.classList.add('hidden');
    this.tryAgainBtn?.classList.add('hidden');
    this.skipBtn?.classList.add('hidden');

    // Show appropriate state and buttons
    switch (state) {
      case 'ready':
        this.readyState?.classList.remove('hidden');
        this.startBtn?.classList.remove('hidden');
        this.skipBtn?.classList.remove('hidden');
        break;
      case 'countdown':
        this.countdownState?.classList.remove('hidden');
        break;
      case 'singing':
        this.singingState?.classList.remove('hidden');
        this.skipBtn?.classList.remove('hidden');
        this.screenElement?.classList.add('singing');
        break;
      case 'results':
        this.resultsState?.classList.remove('hidden');
        this.continueBtn?.classList.remove('hidden');
        this.tryAgainBtn?.classList.remove('hidden');
        this.screenElement?.classList.remove('singing');
        break;
    }
  }

  private showError(message: string): void {
    this.updateUI('ready');
    this.readyState?.classList.add('hidden');
    this.errorState?.classList.remove('hidden');
    this.startBtn?.classList.add('hidden');
    this.skipBtn?.classList.remove('hidden');

    if (this.errorMessage) {
      this.errorMessage.textContent = message;
    }
  }

  private handleStateChange(state: State): void {
    console.log('Voice detection state:', state);
    this.updateUI(state);
  }

  private handleCountdownTick(count: number): void {
    if (this.countdownDisplay) {
      this.countdownDisplay.textContent = String(count);
      // Trigger animation by removing and re-adding class
      this.countdownDisplay.classList.remove('countdown-animate');
      void this.countdownDisplay.offsetWidth; // Force reflow
      this.countdownDisplay.classList.add('countdown-animate');
    }
  }

  private handlePitchDetected(frequency: number, _confidence: number, noteName: string): void {
    if (this.pitchNote) {
      this.pitchNote.textContent = noteName;
    }
    if (this.pitchFrequency) {
      this.pitchFrequency.textContent = `${Math.round(frequency)} Hz`;
    }
  }

  private handleWordAdvance(wordIndex: number, state: WordState): void {
    if (!this.lyricsContainer) return;

    const wordElement = this.lyricsContainer.querySelector(`[data-word-index="${wordIndex}"]`);
    if (!wordElement) return;

    // Remove all state classes
    wordElement.classList.remove('lyric-word--upcoming', 'lyric-word--current', 'lyric-word--completed', 'lyric-word--skipped');

    // Add appropriate class
    wordElement.classList.add(`lyric-word--${state}`);
  }

  private handleNameDetected(name: string, wordIndex: number): void {
    if (!this.lyricsContainer) return;

    // Find the word element for [name] and replace its text with the detected name
    const wordElement = this.lyricsContainer.querySelector(`[data-word-index="${wordIndex}"]`);
    if (wordElement) {
      wordElement.textContent = name;
      console.log(`[UI] Replaced [name] with "${name}" at word index ${wordIndex}`);
    }
  }

  private handleAudioLevel(level: number): void {
    if (this.audioLevelBar) {
      // Convert level to percentage (level is typically 0-0.5 for normal speech)
      const percentage = Math.min(level * 200, 100);
      this.audioLevelBar.style.width = `${percentage}%`;
    }
  }

  private handleResult(result: SingAlongResult): void {
    // Store detected voice type
    if (result.classification) {
      const voiceTypeKey = result.classification.key;
      const pitchNote = result.typicalPitchNote || '';

      // Get content based on user's gender and voice type key
      const content = this.getVoiceTypeContent(voiceTypeKey);
      const voiceTypeTitle = content?.title || voiceTypeKey;

      this.deps?.userRepository.setAnswer('detectedVoiceType', voiceTypeTitle);
      this.deps?.userRepository.setAnswer('detectedVoiceTypeKey', voiceTypeKey);
      this.deps?.userRepository.setAnswer('detectedPitch', pitchNote);

      // Sync with Alpine store
      if ((window as any).syncAlpineStore) {
        (window as any).syncAlpineStore('detectedVoiceType', voiceTypeTitle);
        (window as any).syncAlpineStore('detectedVoiceTypeKey', voiceTypeKey);
        (window as any).syncAlpineStore('detectedPitch', pitchNote);
      }

      // Track analytics
      this.deps?.statsManager.track('voice_detection_completed', {
        voiceType: voiceTypeTitle,
        voiceTypeKey,
        typicalPitch: result.typicalPitch || 0,
        typicalPitchNote: pitchNote,
        minFrequency: result.minFrequency,
        maxFrequency: result.maxFrequency,
      });

      // Update UI with result
      this.renderResult(result);

      // Play celebration animation
      playCelebration();
    } else {
      // No result - show default message
      if (this.resultVoiceType) {
        this.resultVoiceType.textContent = 'Unknown';
      }
      if (this.resultDescription) {
        this.resultDescription.textContent = "We couldn't determine your voice type. Try again or continue to get personalized recommendations.";
      }
    }
  }

  /**
   * Get voice type content based on user's gender and voice type key
   * Uses MALE/FEMALE content maps which include extended ranges
   */
  private getVoiceTypeContent(voiceTypeKey: string): VoiceTypeContent | null {
    // Get user's selected voice/gender from repository
    const userVoice = this.deps?.userRepository.getAnswer('voice') || 'male';
    const isFemale = userVoice === 'female';

    // Select content map based on gender
    const contentMap = isFemale ? VOICE_TYPE_CONTENT.FEMALE : VOICE_TYPE_CONTENT.MALE;

    // Get content for the voice type key
    const content = contentMap[voiceTypeKey as keyof typeof contentMap];
    return content || null;
  }

  private renderResult(result: SingAlongResult): void {
    if (result.classification) {
      const voiceTypeKey = result.classification.key;
      const content = this.getVoiceTypeContent(voiceTypeKey);

      if (this.resultVoiceType) {
        this.resultVoiceType.textContent = content?.title || voiceTypeKey;
      }
      if (this.resultPitchNote) {
        const noteSpan = this.resultPitchNote.querySelector('span');
        if (noteSpan) {
          noteSpan.textContent = result.typicalPitchNote || '--';
        } else {
          this.resultPitchNote.textContent = `Typical pitch: ${result.typicalPitchNote || '--'}`;
        }
      }
      if (this.resultDescription && content) {
        this.resultDescription.textContent = content.description;
      }
      if (this.resultVibe && content) {
        this.resultVibe.textContent = content.vibe;
      }
      if (this.resultExamples && content) {
        this.resultExamples.textContent = content.examples;
      }
      if (this.resultExplanation) {
        if (content?.explanation) {
          this.resultExplanation.textContent = content.explanation;
          this.resultExplanation.classList.remove('hidden');
        } else {
          this.resultExplanation.classList.add('hidden');
        }
      }
      if (this.resultPromise && content) {
        this.resultPromise.textContent = content.promise;
      }
    }
  }

  private handleError(error: string): void {
    console.error('Voice detection error:', error);

    // Track error
    this.deps?.statsManager.track('voice_detection_error', { error });

    // Show error UI with fallback option
    this.showError(error);
  }

  /**
   * Start the detection process
   */
  async start(): Promise<void> {
    if (!this.singAlong) {
      this.createSingAlong();
      this.renderLyrics();
    }

    // Track analytics
    this.deps?.statsManager.track('voice_detection_started', {});

    try {
      await this.singAlong?.start();
    } catch (error) {
      this.handleError((error as Error).message);
    }
  }

  /**
   * Finish early (user clicks "Finish" button)
   */
  finish(): void {
    this.singAlong?.finish();
  }

  /**
   * Restart detection
   */
  restart(): void {
    this.singAlong?.restart();
    this.renderLyrics();
    this.updateUI('ready');
  }

  /**
   * Continue to next screen
   */
  continue(): void {
    const flowManager = (window as any).FlowManager;
    flowManager?.goToNext();
  }

  private destroy(): void {
    this.singAlong?.destroy();
    this.singAlong = null;
    this.screenElement?.classList.remove('singing');
  }
}

// Create singleton instance
const voiceDetectionManager = new VoiceDetectionManager();

// Make globally available
if (typeof window !== 'undefined') {
  (window as any).VoiceDetectionManager = voiceDetectionManager;
}

export default voiceDetectionManager;
